(function () {
  'use strict';

  var TAG_NAME = 'em-inline-search';
  var CONTRACT_VERSION = 2;
  var MIN_QUERY_LENGTH = 2;
  var MAX_OPTIONS = 10;
  var DEBOUNCE_MS = 200;
  var SKELETON_DELAY_MS = 120;
  var LOADING_ANNOUNCE_MS = 300;
  var REQUEST_TIMEOUT_MS = 2500;
  var VISUAL_RESET_TIMEOUT_MS = 1000;
  var CACHE_FRESH_MS = 60000;
  var CACHE_STALE_MS = 300000;
  var CACHE_MAX_ENTRIES = 30;
  var CACHE_MAX_BYTES = 250000;

  if (!window.customElements || customElements.get(TAG_NAME)) return;

  function codePointLength(value) {
    return Array.from(value).length;
  }

  function sameOriginSearchPath(value) {
    try {
      var url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:products|collections|search)(?:\/|$)/i.test(url.pathname);
    } catch (error) {
      return false;
    }
  }

  function createElement(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  class EmperorInlineSearch extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.mode = 'text';
      this.state = 'idle';
      this.revision = 0;
      this.intentRevision = 0;
      this.activeIndex = -1;
      this.composing = false;
      this.cache = new Map();
      this.cacheBytes = 0;
      this.requestController = null;
      this.requestTimeout = null;
      this.debounceTimer = null;
      this.skeletonTimer = null;
      this.loadingTimer = null;
      this.positionFrame = null;
      this.listeners = null;
      this.closePromise = null;
      this.visualReturnTarget = null;
      this.visualCloseReason = null;
      this.visualCloseReturnFocus = false;
      this.disconnectPending = false;
      this.ownerHeader = null;
      this.deferredSubmitPromise = null;
      this.deferredNavigationPromise = null;
      this.visualLastFocused = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('[data-em-inline-search-form]');
      this.input = this.querySelector('input[type="search"]');
      this.anchor = this.querySelector('[data-em-inline-search-anchor]');
      this.clearButton = this.querySelector('[data-em-inline-search-clear]');
      this.submitButton = this.form && this.form.querySelector('button[type="submit"]');
      this.status = this.querySelector('[data-em-inline-search-status]');
      this.layer = this.querySelector('[data-em-inline-search-layer]');
      this.content = this.querySelector('[data-em-inline-search-content]');
      this.visualSlot = this.querySelector('[data-emperor-search-visual-slot]');
      if (!this.form || !this.input || !this.anchor || !this.layer || !this.content || !this.visualSlot) return;

      this.initialized = true;
      this.ownerHeader = this.closest('[data-mag-header]');
      this.listeners = new AbortController();
      var signal = this.listeners.signal;
      this.input.addEventListener('input', this.onInput.bind(this), { signal: signal });
      this.input.addEventListener('focus', this.onFocus.bind(this), { signal: signal });
      this.input.addEventListener('keydown', this.onKeydown.bind(this), { signal: signal });
      this.input.addEventListener('compositionstart', this.onCompositionStart.bind(this), { signal: signal });
      this.input.addEventListener('compositionend', this.onCompositionEnd.bind(this), { signal: signal });
      this.clearButton.addEventListener('click', this.clear.bind(this), { signal: signal });
      this.form.addEventListener('submit', this.onSubmit.bind(this), { signal: signal });
      this.layer.addEventListener('click', this.onLayerClick.bind(this), { signal: signal });
      document.addEventListener('pointerdown', this.onDocumentPointerDown.bind(this), { capture: true, signal: signal });
      document.addEventListener('keydown', this.onDocumentKeydown.bind(this), { capture: true, signal: signal });
      document.addEventListener('focusin', this.onDocumentFocusIn.bind(this), { capture: true, signal: signal });
      document.addEventListener('em:surface-opening', this.onExternalSurfaceOpening.bind(this), { signal: signal });
      window.addEventListener('resize', this.schedulePosition.bind(this), { passive: true, signal: signal });
      window.addEventListener('scroll', this.schedulePosition.bind(this), { passive: true, signal: signal });
      window.addEventListener('pageshow', this.onPageShow.bind(this), { signal: signal });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.schedulePosition.bind(this), { passive: true, signal: signal });
        window.visualViewport.addEventListener('scroll', this.schedulePosition.bind(this), { passive: true, signal: signal });
      }

      document.body.appendChild(this.layer);
      this.updateClearButton();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.disconnectPending = true;
      Promise.resolve(this.close('section-disconnect', false)).then(function (receipt) {
        if (!receipt || !receipt.blocked) this.finishDisconnect();
      }.bind(this));
    }

    finishDisconnect() {
      if (!this.disconnectPending) return;
      this.disconnectPending = false;
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
      this.abortRequest();
      this.clearTimers();
      if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
      if (this.listeners) this.listeners.abort();
      if (this.ownerHeader) this.ownerHeader.classList.remove('is-search-open');
      if (this.layer && this.layer.parentNode) this.appendChild(this.layer);
      this.initialized = false;
      this.ownerHeader = null;
      if (this.isConnected) this.connectedCallback();
    }

    get isOpen() {
      return Boolean(this.layer && !this.layer.hidden);
    }

    get query() {
      return this.input ? this.input.value.trim() : '';
    }

    onCompositionStart() {
      this.composing = true;
    }

    onCompositionEnd() {
      this.composing = false;
      this.onInput();
    }

    onInput() {
      this.updateClearButton();
      this.invalidatePendingWork('input-change');
      if (this.mode === 'visual') return;
      if (this.composing) return;
      var query = this.query;
      if (codePointLength(query) < MIN_QUERY_LENGTH) {
        this.close('query-too-short', false);
        this.state = 'idle';
        return;
      }

      var self = this;
      var intentRevision = this.intentRevision;
      this.skeletonTimer = window.setTimeout(function () {
        if (intentRevision !== self.intentRevision || query !== self.query) return;
        self.renderLoading();
      }, SKELETON_DELAY_MS);
      this.loadingTimer = window.setTimeout(function () {
        if (intentRevision !== self.intentRevision || query !== self.query) return;
        self.announce(self.dataset.loadingText || 'Loading');
      }, LOADING_ANNOUNCE_MS);
      this.debounceTimer = window.setTimeout(function () {
        if (intentRevision !== self.intentRevision || query !== self.query) return;
        self.load(query, 'input', intentRevision);
      }, DEBOUNCE_MS);
    }

    onFocus() {
      if (this.mode === 'visual') return;
      document.dispatchEvent(
        new CustomEvent('em:surface-opening', { detail: { surface: 'search' } })
      );
      if (codePointLength(this.query) < MIN_QUERY_LENGTH) return;
      var cached = this.getCache(this.cacheKey(this.query));
      if (cached) {
        this.renderMarkup(cached.markup, cached.stale ? 'stale' : 'results', 'focus');
        if (cached.stale) this.load(this.query, 'stale-refresh');
        return;
      }
      this.load(this.query, 'focus');
    }

    onKeydown(event) {
      if (event.key === 'Escape' && (this.isOpen || this.hasPendingWork())) {
        event.preventDefault();
        this.close('escape', true);
        return;
      }

      if (event.key === 'Tab') {
        this.close('tab', false);
        return;
      }

      if (!this.isOpen) return;
      var options = this.options();
      if (!options.length) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.setActive(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.setActive(options.length - 1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        var action = options[this.activeIndex].querySelector('a[href], button');
        if (action) {
          event.preventDefault();
          action.click();
        }
      }
    }

    onDocumentKeydown(event) {
      if (this.mode !== 'visual' || (!this.isOpen && !this.closePromise)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close('visual-escape', true);
        return;
      }
      if (event.key !== 'Tab') return;

      var focusables = this.getVisualFocusables();
      if (!focusables.length) {
        event.preventDefault();
        this.layer.focus({ preventScroll: true });
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      var currentIndex = focusables.indexOf(document.activeElement);
      var direction = event.shiftKey ? -1 : 1;
      var nextIndex = currentIndex === -1
        ? (event.shiftKey ? focusables.length - 1 : 0)
        : (currentIndex + direction + focusables.length) % focusables.length;
      focusables[nextIndex].focus({ preventScroll: true });
    }

    onDocumentFocusIn(event) {
      if (this.mode !== 'visual' || (!this.isOpen && !this.closePromise)) return;
      if (this.isVisualFocusTarget(event.target)) {
        this.visualLastFocused = event.target;
        return;
      }
      this.focusVisualFallback();
    }

    getVisualFocusables() {
      var selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      var candidates = Array.from(this.visualSlot.querySelectorAll(selector));
      candidates = candidates.concat(
        Array.from(this.layer.querySelectorAll('[data-em-visual-cleanup-retry]:not([disabled])'))
      );
      return candidates.filter(function (element, index, all) {
        return all.indexOf(element) === index &&
          element.isConnected &&
          !element.hidden &&
          element.getAttribute('aria-hidden') !== 'true' &&
          element.getClientRects().length > 0;
      });
    }

    isVisualFocusTarget(target) {
      return Boolean(
        target &&
        (this.visualSlot.contains(target) ||
          (this.layer.contains(target) && target.matches('[data-em-visual-cleanup-retry]')) ||
          target === this.layer)
      );
    }

    focusVisualFallback() {
      var focusables = this.getVisualFocusables();
      var target = this.visualLastFocused && focusables.includes(this.visualLastFocused)
        ? this.visualLastFocused
        : focusables[0] || this.layer;
      if (target && target.isConnected && document.activeElement !== target) {
        target.focus({ preventScroll: true });
      }
    }

    onSubmit(event) {
      if (this.mode === 'visual') {
        event.preventDefault();
        if (this.deferredSubmitPromise) return;
        this.deferredSubmitPromise = this.close('submit', false)
          .then(function (receipt) {
            if (receipt && receipt.closed && !receipt.blocked) {
              HTMLFormElement.prototype.submit.call(this.form);
            }
            return receipt;
          }.bind(this))
          .finally(function () {
            this.deferredSubmitPromise = null;
          }.bind(this));
        return;
      }

      if (!this.query) {
        event.preventDefault();
        return;
      }

      var options = this.options();
      if (this.activeIndex >= 0 && options[this.activeIndex]) {
        var action = options[this.activeIndex].querySelector('a[href], button');
        if (action) {
          event.preventDefault();
          action.click();
          return;
        }
      }

      this.close('submit', false);
    }

    onLayerClick(event) {
      var cleanupRetry = event.target.closest('[data-em-visual-cleanup-retry]');
      if (cleanupRetry) {
        event.preventDefault();
        this.close(this.visualCloseReason || 'visual-cleanup-retry', this.visualCloseReturnFocus);
        return;
      }

      var retry = event.target.closest('[data-em-inline-search-retry]');
      if (retry) {
        event.preventDefault();
        this.load(this.query, 'retry');
        this.input.focus();
        return;
      }

      var link = event.target.closest('a[href]');
      if (link && this.layer.contains(link)) {
        if (this.mode === 'visual') {
          event.preventDefault();
          if (this.deferredNavigationPromise) return;
          var destination = link.href;
          this.deferredNavigationPromise = this.close('selection', false)
            .then(function (receipt) {
              if (receipt && receipt.closed && !receipt.blocked) window.location.assign(destination);
              return receipt;
            })
            .finally(function () {
              this.deferredNavigationPromise = null;
            }.bind(this));
          return;
        }
        this.close('selection', false);
      }
    }

    onDocumentPointerDown(event) {
      if (this.contains(event.target) || this.layer.contains(event.target)) return;
      this.close('click-away', false);
    }

    onExternalSurfaceOpening(event) {
      if (event.detail && event.detail.surface === 'search') return;
      this.close('external-surface', false);
    }

    onPageShow(event) {
      if (event.persisted) this.close('page-restore', false);
    }

    clear() {
      if (this.mode === 'visual') {
        return this.close('clear', false).then(function (receipt) {
          if (receipt && receipt.closed && !receipt.blocked) this.finishClear();
          return receipt;
        }.bind(this));
      }
      this.close('clear', false);
      this.finishClear();
      return Promise.resolve({ closed: true, blocked: false, reason: 'clear' });
    }

    finishClear() {
      this.input.value = '';
      this.updateClearButton();
      this.state = 'idle';
      this.announce('Search cleared.');
      this.input.focus();
    }

    updateClearButton() {
      if (this.clearButton) this.clearButton.hidden = !this.query;
    }

    cacheKey(query) {
      return [document.documentElement.lang || 'en', query.toLocaleLowerCase()].join(':');
    }

    getCache(key) {
      var cached = this.cache.get(key);
      if (!cached) return null;
      var age = Date.now() - cached.createdAt;
      if (age > CACHE_STALE_MS) {
        this.deleteCache(key);
        return null;
      }
      this.cache.delete(key);
      this.cache.set(key, cached);
      return { markup: cached.markup, stale: age > CACHE_FRESH_MS };
    }

    setCache(key, markup) {
      var size = new Blob([markup]).size;
      if (size > CACHE_MAX_BYTES) return;
      if (this.cache.has(key)) this.deleteCache(key);
      this.cache.set(key, { markup: markup, createdAt: Date.now(), size: size });
      this.cacheBytes += size;
      while (this.cache.size > CACHE_MAX_ENTRIES || this.cacheBytes > CACHE_MAX_BYTES) {
        this.deleteCache(this.cache.keys().next().value);
      }
    }

    deleteCache(key) {
      var cached = this.cache.get(key);
      if (!cached) return;
      this.cacheBytes -= cached.size;
      this.cache.delete(key);
    }

    load(query, reason, intentRevision) {
      if (codePointLength(query) < MIN_QUERY_LENGTH) return;
      if (typeof intentRevision === 'number' && intentRevision !== this.intentRevision) return;
      var key = this.cacheKey(query);
      var cached = this.getCache(key);
      if (cached && !cached.stale && reason !== 'retry') {
        this.clearTimers();
        this.renderMarkup(cached.markup, 'results', reason);
        return;
      }

      if (cached && cached.stale && reason !== 'stale-refresh' && reason !== 'retry') {
        this.renderMarkup(cached.markup, 'stale', reason);
      } else if (!this.isOpen) {
        this.renderLoading();
      }

      this.abortRequest();
      this.revision += 1;
      var revision = this.revision;
      this.requestController = new AbortController();
      var requestUrl = new URL(this.dataset.predictiveSearchUrl, window.location.origin);
      requestUrl.searchParams.set('q', query);
      requestUrl.searchParams.set('resources[type]', 'query,product,collection');
      requestUrl.searchParams.set('resources[limit]', '10');
      requestUrl.searchParams.set('resources[limit_scope]', 'all');
      requestUrl.searchParams.set('resources[options][unavailable_products]', 'last');
      requestUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title');
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      var self = this;
      var timedOut = false;
      this.requestTimeout = window.setTimeout(function () {
        timedOut = true;
        self.requestController.abort();
      }, REQUEST_TIMEOUT_MS);

      fetch(requestUrl.toString(), {
        method: 'GET',
        headers: { Accept: 'text/html' },
        signal: this.requestController.signal,
      })
        .then(function (response) {
          if (!response.ok) throw new Error('http-' + response.status);
          return response.text();
        })
        .then(function (text) {
          if (revision !== self.revision || query !== self.query) return;
          var markup = self.parseResponse(text);
          self.setCache(key, markup);
          self.clearTimers();
          self.renderMarkup(markup, 'results', reason);
        })
        .catch(function (error) {
          if (revision !== self.revision || query !== self.query) return;
          if (error.name === 'AbortError' && !timedOut) return;
          self.clearTimers();
          if (cached) {
            self.renderMarkup(cached.markup, 'stale', 'refresh-error');
            self.announce('Recent search suggestions are shown. Refresh is unavailable.');
          } else {
            self.renderError(timedOut ? 'timeout' : 'request-error');
          }
        })
        .finally(function () {
          if (revision === self.revision) {
            window.clearTimeout(self.requestTimeout);
            self.requestTimeout = null;
            self.requestController = null;
          }
        });
    }

    parseResponse(text) {
      var documentFragment = new DOMParser().parseFromString(text, 'text/html');
      var section = documentFragment.querySelector('#shopify-section-' + this.dataset.sectionId);
      var results = section && section.querySelector('[data-em-inline-search-results]');
      if (!results) throw new Error('invalid-markup');

      var options = Array.from(results.querySelectorAll('[role="option"]'));
      if (options.length > MAX_OPTIONS) {
        options.slice(MAX_OPTIONS).forEach(function (option) {
          option.remove();
        });
        results.dataset.resultCount = String(MAX_OPTIONS);
      }

      Array.from(results.querySelectorAll('a[href]')).forEach(function (link) {
        if (!sameOriginSearchPath(link.getAttribute('href'))) throw new Error('invalid-destination');
      });
      return results.outerHTML;
    }

    renderLoading() {
      this.state = 'loading';
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.content.replaceChildren();
      var shell = createElement('div', 'em-inline-results em-inline-results--loading');
      shell.setAttribute('aria-hidden', 'true');
      for (var index = 0; index < 3; index += 1) {
        shell.appendChild(createElement('span', 'em-inline-results__skeleton'));
      }
      this.content.appendChild(shell);
      this.open('loading');
    }

    renderMarkup(markup, state, reason) {
      var documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      var results = documentFragment.querySelector('[data-em-inline-search-results]');
      if (!results) {
        this.renderError('invalid-markup');
        return;
      }
      this.state = state;
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.content.replaceChildren(results);
      if (state === 'stale') {
        var notice = createElement('p', 'em-inline-results__notice', 'Recent suggestions');
        results.prepend(notice);
      }
      this.open(reason || state);
      var nativeCount = Number(results.dataset.nativeResultCount || 0);
      this.announce(
        nativeCount === 0
          ? 'No search suggestions. View all results to continue.'
          : nativeCount + (nativeCount === 1 ? ' suggestion available.' : ' suggestions available.')
      );
    }

    renderError(failureClass) {
      this.state = 'error';
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.content.replaceChildren();
      var region = createElement('div', 'em-inline-results em-inline-results--error');
      region.setAttribute('role', 'alert');
      region.appendChild(createElement('p', 'em-inline-results__message', this.dataset.errorText));
      var actions = createElement('div', 'em-inline-results__error-actions');
      actions.setAttribute('role', 'group');
      actions.setAttribute('aria-label', 'Search recovery');
      var retryOption = createElement('div', 'em-inline-results__error-option');
      retryOption.id = 'EmInlineRetryOption';
      retryOption.setAttribute('role', 'option');
      retryOption.setAttribute('aria-selected', 'false');
      var retry = createElement('button', 'em-inline-results__retry', 'Try again');
      retry.type = 'button';
      retry.tabIndex = -1;
      retry.setAttribute('data-em-inline-search-retry', '');
      retryOption.appendChild(retry);
      var fullOption = createElement('div', 'em-inline-results__error-option');
      fullOption.id = 'EmInlineErrorFullSearchOption';
      fullOption.setAttribute('role', 'option');
      fullOption.setAttribute('aria-selected', 'false');
      var full = createElement('a', 'em-inline-results__full-link', 'View full search');
      full.href = this.form.action + '?q=' + encodeURIComponent(this.query) + '&options%5Bprefix%5D=last';
      full.tabIndex = -1;
      fullOption.appendChild(full);
      actions.append(retryOption, fullOption);
      region.appendChild(actions);
      this.content.appendChild(region);
      this.layer.dataset.failureClass = failureClass;
      this.open('error');
      this.announce(this.dataset.errorText + ' Use the arrow keys to retry or view the full search.');
    }

    activateVisual(source, returnTarget) {
      if (!this.visualSlot.firstElementChild || this.closePromise) return false;
      this.invalidatePendingWork('visual-activate');
      var wasClosed = !this.isOpen;
      this.mode = 'visual';
      this.state = 'visual';
      this.visualReturnTarget = returnTarget && returnTarget.isConnected ? returnTarget : this.input;
      this.visualLastFocused = null;
      this.visualCloseReason = null;
      this.visualCloseReturnFocus = false;
      this.layer.hidden = false;
      this.layer.dataset.visualClosePhase = 'active';
      this.input.setAttribute('aria-expanded', 'true');
      this.setVisualContainment(true);
      this.position();
      if (wasClosed) {
        this.dispatchLifecycle('overlay:opened', {
          contractVersion: CONTRACT_VERSION,
          mode: 'visual',
          reason: 'visual-activate',
          visualPlacement: source || 'secondary',
        });
      }
      this.dispatchVisualCommand('visual:activate', {
        source: source || 'secondary',
      });
      window.requestAnimationFrame(function () {
        if (this.mode === 'visual' && this.isOpen && !this.closePromise) {
          this.dispatchVisualCommand('visual:focus-entry', {
            reason: 'visual-activate',
          });
        }
      }.bind(this));
      return true;
    }

    open(reason) {
      if (this.mode === 'visual') return;
      if (!this.anchor || !this.anchor.getClientRects().length) {
        this.close('missing-anchor', false);
        return;
      }
      this.mode = 'text';
      this.setVisualContainment(false);
      var wasClosed = !this.isOpen;
      this.layer.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.position();
      if (wasClosed) {
        this.dispatchLifecycle('text-search:activated', {
          contractVersion: CONTRACT_VERSION,
          reason: reason,
        });
        this.dispatchLifecycle('overlay:opened', {
          contractVersion: CONTRACT_VERSION,
          mode: 'text',
          reason: reason,
          visualPlacement: 'absent',
        });
      }
    }

    close(reason, returnFocus) {
      this.invalidatePendingWork(reason || 'dismiss');
      if (this.closePromise) return this.closePromise;

      var wasOpen = this.isOpen;
      var wasVisual = this.mode === 'visual';
      if (!wasOpen && !wasVisual) {
        return Promise.resolve({ closed: true, blocked: false, wasOpen: false, reason: reason });
      }

      if (wasOpen) {
        this.dispatchLifecycle('overlay:closing', {
          contractVersion: CONTRACT_VERSION,
          mode: this.mode,
          reason: reason,
        });
      }

      if (!wasVisual) {
        return Promise.resolve(this.completeClose(reason, returnFocus, false, { cleaned: true }));
      }

      this.visualCloseReason = reason;
      this.visualCloseReturnFocus = Boolean(returnFocus);
      this.layer.dataset.visualClosePhase = 'awaiting-reset';
      var self = this;
      this.closePromise = this.resetVisual(reason)
        .then(function (resetReceipt) {
          if (!resetReceipt.cleaned) {
            self.renderCleanupError(resetReceipt.failureClass, reason);
            return {
              closed: false,
              blocked: true,
              reason: reason,
              reset: resetReceipt,
            };
          }
          self.layer.dataset.visualClosePhase = 'reset-acknowledged';
          return self.completeClose(reason, returnFocus, true, resetReceipt);
        })
        .finally(function () {
          self.closePromise = null;
        });
      return this.closePromise;
    }

    completeClose(reason, returnFocus, wasVisual, resetReceipt) {
      this.layer.hidden = true;
      this.layer.removeAttribute('data-failure-class');
      this.layer.dataset.visualClosePhase = 'closed';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.clearActive();
      this.setVisualContainment(false);
      this.mode = 'text';
      this.state = 'idle';
      this.dispatchLifecycle('overlay:closed', {
        contractVersion: CONTRACT_VERSION,
        mode: wasVisual ? 'visual' : 'text',
        reason: reason,
      });
      var focusTarget = wasVisual && this.visualReturnTarget && this.visualReturnTarget.isConnected
        ? this.visualReturnTarget
        : this.input;
      this.visualReturnTarget = null;
      this.visualLastFocused = null;
      this.visualCloseReason = null;
      this.visualCloseReturnFocus = false;
      if (returnFocus && focusTarget && focusTarget.isConnected) {
        focusTarget.focus({ preventScroll: true });
      }
      if (this.disconnectPending) this.finishDisconnect();
      return {
        closed: true,
        blocked: false,
        wasOpen: true,
        reason: reason,
        reset: resetReceipt,
      };
    }

    setVisualContainment(active) {
      document.body.classList.toggle('em-inline-search-visual-locked', active);
      this.layer.dataset.mode = active ? 'visual' : 'text';
      this.form.toggleAttribute('data-em-text-controls-disabled', active);
      this.input.disabled = active;
      this.clearButton.disabled = active;
      if (this.submitButton) this.submitButton.disabled = active;
      if (active) {
        this.layer.tabIndex = -1;
        this.layer.setAttribute('role', 'dialog');
        this.layer.setAttribute('aria-label', 'Photo search');
        this.layer.setAttribute('aria-modal', 'true');
      } else {
        this.layer.removeAttribute('tabindex');
        this.layer.removeAttribute('role');
        this.layer.removeAttribute('aria-label');
        this.layer.removeAttribute('aria-modal');
      }
    }

    renderCleanupError(failureClass, reason) {
      this.state = 'cleanup-error';
      this.layer.hidden = false;
      this.layer.dataset.failureClass = failureClass || 'visual-cleanup-failed';
      this.layer.dataset.visualClosePhase = 'cleanup-error';
      this.content.replaceChildren();
      var region = createElement('div', 'em-inline-results em-inline-results--error');
      region.setAttribute('role', 'alert');
      region.appendChild(
        createElement(
          'p',
          'em-inline-results__message',
          'Photo search could not close safely. Retry cleanup or reload this page.'
        )
      );
      var retry = createElement('button', 'em-inline-results__retry', 'Retry cleanup');
      retry.type = 'button';
      retry.setAttribute('data-em-visual-cleanup-retry', '');
      region.appendChild(retry);
      this.content.appendChild(region);
      this.announce('Photo search cleanup failed. Retry cleanup or reload this page.');
      this.visualCloseReason = reason;
      if (!this.isVisualFocusTarget(document.activeElement)) {
        window.requestAnimationFrame(this.focusVisualFallback.bind(this));
      }
    }

    dispatchLifecycle(name, detail) {
      this.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          composed: true,
          detail: detail,
        })
      );
    }

    dispatchVisualCommand(name, detail) {
      this.visualSlot.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          composed: true,
          detail: Object.assign({ contractVersion: CONTRACT_VERSION }, detail || {}),
        })
      );
    }

    resetVisual(reason) {
      if (!this.visualSlot.firstElementChild) {
        return Promise.resolve({ cleaned: true, failureClass: null, source: 'no-child' });
      }
      var self = this;
      return new Promise(function (resolve) {
        var settled = false;
        var timeout = window.setTimeout(function () {
          if (settled) return;
          settled = true;
          resolve({ cleaned: false, failureClass: 'visual-cleanup-timeout', source: 'timeout' });
        }, VISUAL_RESET_TIMEOUT_MS);
        self.dispatchVisualCommand('visual:reset', {
          reason: reason,
          acknowledge: function (receipt) {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            var cleaned = Boolean(receipt && receipt.cleaned);
            resolve({
              cleaned: cleaned,
              failureClass: cleaned
                ? null
                : (receipt && receipt.failureClass) || 'visual-cleanup-rejected',
              source: 'acknowledgement',
            });
          },
        });
      });
    }

    options() {
      if (!this.content) return [];
      return Array.from(this.content.querySelectorAll('[role="option"]'));
    }

    moveActive(delta) {
      var options = this.options();
      if (!options.length) return;
      var next = this.activeIndex < 0
        ? (delta > 0 ? 0 : options.length - 1)
        : (this.activeIndex + delta + options.length) % options.length;
      this.setActive(next);
    }

    setActive(index) {
      var options = this.options();
      if (!options[index]) return;
      options.forEach(function (option, optionIndex) {
        option.setAttribute('aria-selected', optionIndex === index ? 'true' : 'false');
      });
      this.activeIndex = index;
      this.input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    }

    clearActive() {
      this.options().forEach(function (option) {
        option.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(function () {
        this.status.textContent = message || '';
      }.bind(this));
    }

    schedulePosition() {
      if (!this.isOpen || this.positionFrame) return;
      this.positionFrame = window.requestAnimationFrame(function () {
        this.positionFrame = null;
        this.position();
      }.bind(this));
    }

    position() {
      if (!this.isOpen) return;
      var rect = this.anchor.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        this.close('missing-anchor', false);
        return;
      }
      var breakpointWidth = window.innerWidth;
      var visualViewport = window.visualViewport;
      var viewportLeft = visualViewport ? visualViewport.offsetLeft : 0;
      var viewportTop = visualViewport ? visualViewport.offsetTop : 0;
      var viewportWidth = visualViewport ? visualViewport.width : document.documentElement.clientWidth;
      var viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
      var viewportRight = viewportLeft + viewportWidth;
      var viewportBottom = viewportTop + viewportHeight;
      var gutter = breakpointWidth <= 479 ? 8 : breakpointWidth <= 767 ? 12 : 24;
      var horizontalGutter = Math.min(gutter, Math.max(0, viewportWidth / 2));
      var verticalGutter = Math.min(gutter, Math.max(0, viewportHeight / 2));
      var availableWidth = Math.max(0, viewportWidth - horizontalGutter * 2);
      var width = breakpointWidth <= 1400
        ? Math.min(rect.width, availableWidth)
        : Math.min(680, availableWidth);
      var left = breakpointWidth <= 1400 ? rect.left : rect.right - width;
      var minimumLeft = viewportLeft + horizontalGutter;
      var maximumLeft = Math.max(minimumLeft, viewportRight - horizontalGutter - width);
      left = Math.max(minimumLeft, Math.min(left, maximumLeft));
      var right = Math.min(viewportRight - horizontalGutter, left + width);
      var minimumTop = viewportTop + verticalGutter;
      var maximumTop = Math.max(minimumTop, viewportBottom - verticalGutter);
      var top = Math.min(maximumTop, Math.max(minimumTop, rect.bottom + 6));
      var bottom = Math.max(top, viewportBottom - verticalGutter);
      var appliedLeft = Math.ceil(left);
      var appliedRight = Math.floor(right);
      if (appliedLeft > appliedRight) appliedLeft = appliedRight = right;
      var appliedTop = Math.ceil(top);
      var appliedBottom = Math.floor(bottom);
      if (appliedTop > appliedBottom) appliedTop = appliedBottom = bottom;
      var appliedWidth = Math.max(0, appliedRight - appliedLeft);
      var appliedHeight = Math.max(0, appliedBottom - appliedTop);
      var removeBorder = appliedWidth < 2 || appliedHeight < 2;
      this.layer.style.left = appliedLeft + 'px';
      this.layer.style.top = appliedTop + 'px';
      this.layer.style.width = appliedWidth + 'px';
      this.layer.style.borderWidth = removeBorder ? '0' : '';
      this.layer.style.maxHeight = Math.max(0, appliedHeight - (removeBorder ? 0 : 2)) + 'px';
      this.layer.dataset.anchorTop = String(Math.round(rect.bottom));
      this.layer.dataset.visualViewport = [
        viewportLeft,
        viewportTop,
        viewportWidth,
        viewportHeight,
      ].join(',');
      this.layer.dataset.safeEdges = [appliedLeft, appliedTop, appliedRight, appliedBottom].join(',');
    }

    hasPendingWork() {
      return Boolean(
        this.debounceTimer ||
        this.skeletonTimer ||
        this.loadingTimer ||
        this.requestController ||
        this.requestTimeout
      );
    }

    invalidatePendingWork() {
      this.intentRevision += 1;
      this.clearTimers();
      this.abortRequest();
      if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }

    abortRequest() {
      if (this.requestController) this.requestController.abort();
      this.requestController = null;
      if (this.requestTimeout) window.clearTimeout(this.requestTimeout);
      this.requestTimeout = null;
      this.revision += 1;
    }

    clearTimers() {
      window.clearTimeout(this.debounceTimer);
      window.clearTimeout(this.skeletonTimer);
      window.clearTimeout(this.loadingTimer);
      this.debounceTimer = null;
      this.skeletonTimer = null;
      this.loadingTimer = null;
    }
  }

  customElements.define(TAG_NAME, EmperorInlineSearch);
})();
